import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type TelehealthCallLifecycleGuardState =
  | 'provider_alone_before_anyone_joined'
  | 'with_participants'
  | 'recently_empty'
  | 'empty_kept_open';

interface UseTelehealthCallLifecycleGuardArgs {
  remoteParticipantIds: string[];
  remoteParticipantNames: Map<string, string>;
  emptyGraceMs?: number;
}

const DEFAULT_EMPTY_GRACE_MS = 0;

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export function formatTelehealthParticipantList(names: string[]) {
  const visibleNames = names.map(name => name.trim()).filter(Boolean);

  if (visibleNames.length === 0) {
    return undefined;
  }

  if (visibleNames.length === 1) {
    return visibleNames[0];
  }

  if (visibleNames.length === 2) {
    return `${visibleNames[0]} and ${visibleNames[1]}`;
  }

  return `${visibleNames[0]} and ${visibleNames.length - 1} others`;
}

export function useTelehealthCallLifecycleGuard({
  remoteParticipantIds,
  remoteParticipantNames,
  emptyGraceMs = DEFAULT_EMPTY_GRACE_MS,
}: UseTelehealthCallLifecycleGuardArgs) {
  const [state, setState] = useState<TelehealthCallLifecycleGuardState>(
    'provider_alone_before_anyone_joined',
  );
  const [isNudgeVisible, setIsNudgeVisible] = useState(false);
  const [lastRemoteParticipantNames, setLastRemoteParticipantNames] = useState<string[]>([]);
  const hadRemoteParticipantsRef = useRef(false);
  const stateRef = useRef(state);
  const isNudgeVisibleRef = useRef(isNudgeVisible);
  const emptyTimerRef = useRef<number | undefined>(undefined);

  const clearEmptyTimer = useCallback(() => {
    if (emptyTimerRef.current === undefined) {
      return;
    }

    window.clearTimeout(emptyTimerRef.current);
    emptyTimerRef.current = undefined;
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    isNudgeVisibleRef.current = isNudgeVisible;
  }, [isNudgeVisible]);

  useEffect(() => () => clearEmptyTimer(), [clearEmptyTimer]);

  useEffect(() => {
    if (remoteParticipantIds.length > 0) {
      hadRemoteParticipantsRef.current = true;
      clearEmptyTimer();
      setIsNudgeVisible(false);
      setState('with_participants');
      const nextRemoteParticipantNames = remoteParticipantIds.map(
        (participantId, index) =>
          remoteParticipantNames.get(participantId) ?? `Participant ${index + 1}`,
      );
      setLastRemoteParticipantNames(current =>
        areStringArraysEqual(current, nextRemoteParticipantNames)
          ? current
          : nextRemoteParticipantNames,
      );
      return;
    }

    if (!hadRemoteParticipantsRef.current) {
      clearEmptyTimer();
      setIsNudgeVisible(false);
      setState('provider_alone_before_anyone_joined');
      return;
    }

    if (stateRef.current === 'empty_kept_open') {
      return;
    }

    setState('recently_empty');

    if (isNudgeVisibleRef.current) {
      return;
    }

    if (emptyGraceMs <= 0) {
      clearEmptyTimer();
      setIsNudgeVisible(true);
      return;
    }

    if (emptyTimerRef.current === undefined) {
      emptyTimerRef.current = window.setTimeout(() => {
        setIsNudgeVisible(true);
        emptyTimerRef.current = undefined;
      }, emptyGraceMs);
    }
  }, [clearEmptyTimer, emptyGraceMs, remoteParticipantIds, remoteParticipantNames]);

  const markWaitingForReconnect = useCallback(() => {
    clearEmptyTimer();
    setState('empty_kept_open');
    setIsNudgeVisible(false);
  }, [clearEmptyTimer]);

  const isGuardedEmptyRoom = useMemo(
    () =>
      remoteParticipantIds.length === 0 &&
      (state === 'recently_empty' || state === 'empty_kept_open'),
    [remoteParticipantIds.length, state],
  );

  return {
    state,
    isGuardedEmptyRoom,
    shouldShowEmptyRoomNudge: isGuardedEmptyRoom && isNudgeVisible,
    lastRemoteParticipantNames,
    markWaitingForReconnect,
  };
}
